import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  createFinanceBillingPeriod,
  deleteFinanceBillingPeriod,
  fetchFinanceBillingPeriods,
  fetchFinancePaymentSettings,
  patchFinanceBillingPeriod,
  resetFinanceApiSession,
} from "../../../utils/api/api";
import { normalizeBillingCurrency } from "../../../utils/clubBillingCurrency";
import { financePeriodStorageKey } from "../../../utils/clubScope";

export const FINANCE_SELECTED_PERIOD_STORAGE_KEY =
  "mmacenter:ekonomi:financeSelectedPeriodId:v1";

const FinanceBillingContext = createContext(null);

function readStoredSelectedPeriodId(clubID) {
  const key = financePeriodStorageKey(clubID, FINANCE_SELECTED_PERIOD_STORAGE_KEY);
  try {
    const v = window.localStorage?.getItem(key);
    return v && typeof v === "string" ? v : null;
  } catch {
    return null;
  }
}

function writeStoredSelectedPeriodId(id, clubID) {
  const key = financePeriodStorageKey(clubID, FINANCE_SELECTED_PERIOD_STORAGE_KEY);
  try {
    if (id == null || id === "") {
      window.localStorage?.removeItem(key);
    } else {
      window.localStorage?.setItem(key, id);
    }
  } catch {
    /* ignore */
  }
}

function pickInitialId(periodList, preferredId) {
  if (preferredId && periodList.some((p) => p.id === preferredId)) {
    return preferredId;
  }
  const active = periodList.find((p) => p.isActive);
  if (active) return active.id;
  return periodList[0]?.id ?? "";
}

/**
 * @param {{ children: React.ReactNode; isActive?: boolean; clubID?: number|null }} props
 */
export function FinanceBillingProvider({ children, isActive = true, clubID = null }) {
  const [periods, setPeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  /** @type {'api' | 'empty' | 'error'} */
  const [periodsSource, setPeriodsSource] = useState("empty");
  const [selectedPeriodId, setSelectedPeriodIdState] = useState(() =>
    pickInitialId([], readStoredSelectedPeriodId(clubID))
  );
  const [billingCurrency, setBillingCurrency] = useState("SEK");

  const refreshBillingCurrency = useCallback(async () => {
    try {
      const settings = await fetchFinancePaymentSettings(clubID);
      setBillingCurrency(normalizeBillingCurrency(settings?.billingCurrency, "SEK"));
    } catch {
      setBillingCurrency("SEK");
    }
  }, [clubID]);

  const applyPeriodList = useCallback(
    (list, source) => {
      setPeriods(list);
      setPeriodsSource(source);
      setSelectedPeriodIdState((prev) => {
        const nextId = pickInitialId(list, prev || readStoredSelectedPeriodId(clubID));
        writeStoredSelectedPeriodId(nextId, clubID);
        return nextId;
      });
    },
    [clubID]
  );

  const refreshPeriodsFromApi = useCallback(async () => {
    resetFinanceApiSession();
    setPeriodsLoading(true);
    try {
      const data = await fetchFinanceBillingPeriods(clubID);
      if (data.length > 0) {
        applyPeriodList(data, "api");
        return { source: "api", periods: data };
      }
      applyPeriodList([], "empty");
      return { source: "empty", periods: [] };
    } catch {
      applyPeriodList([], "error");
      return { source: "error", periods: [] };
    } finally {
      setPeriodsLoading(false);
    }
  }, [applyPeriodList, clubID]);

  useEffect(() => {
    if (!isActive) return;
    refreshPeriodsFromApi();
    refreshBillingCurrency();
  }, [isActive, refreshPeriodsFromApi, refreshBillingCurrency, clubID]);

  useEffect(() => {
    if (!isActive || !selectedPeriodId) return;
    writeStoredSelectedPeriodId(selectedPeriodId, clubID);
  }, [isActive, selectedPeriodId, clubID]);

  const setSelectedPeriodId = useCallback(
    (id) => {
      setSelectedPeriodIdState(id);
      writeStoredSelectedPeriodId(id, clubID);
    },
    [clubID]
  );

  const selectedPeriod = useMemo(
    () => periods.find((p) => p.id === selectedPeriodId) ?? null,
    [periods, selectedPeriodId]
  );

  const createBillingPeriod = useCallback(
    async (form) => {
      const created = await createFinanceBillingPeriod(form, clubID);
      await refreshPeriodsFromApi();
      if (created?.id) setSelectedPeriodId(created.id);
      return created;
    },
    [refreshPeriodsFromApi, setSelectedPeriodId, clubID]
  );

  const updateBillingPeriod = useCallback(
    async (id, form) => {
      const updated = await patchFinanceBillingPeriod(id, form, clubID);
      await refreshPeriodsFromApi();
      return updated;
    },
    [refreshPeriodsFromApi, clubID]
  );

  const removeBillingPeriod = useCallback(
    async (id) => {
      await deleteFinanceBillingPeriod(id, clubID);
      await refreshPeriodsFromApi();
    },
    [refreshPeriodsFromApi, clubID]
  );

  const value = useMemo(
    () => ({
      clubID,
      billingCurrency,
      refreshBillingCurrency,
      periods,
      periodsLoading,
      periodsSource,
      selectedPeriodId,
      setSelectedPeriodId,
      selectedPeriod,
      refreshPeriodsFromApi,
      createBillingPeriod,
      updateBillingPeriod,
      removeBillingPeriod,
    }),
    [
      clubID,
      billingCurrency,
      refreshBillingCurrency,
      periods,
      periodsLoading,
      periodsSource,
      selectedPeriodId,
      setSelectedPeriodId,
      selectedPeriod,
      refreshPeriodsFromApi,
      createBillingPeriod,
      updateBillingPeriod,
      removeBillingPeriod,
    ]
  );

  return (
    <FinanceBillingContext.Provider value={value}>
      {children}
    </FinanceBillingContext.Provider>
  );
}

export function useFinanceBilling() {
  const ctx = useContext(FinanceBillingContext);
  if (!ctx) {
    throw new Error("useFinanceBilling must be used within FinanceBillingProvider");
  }
  return ctx;
}
